"use client";

import { useState, useEffect, type ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, LogIn, UserPlus, LogOut, Newspaper } from "lucide-react";
import { SvgMaskIcon } from "@/components/common/svg-mask-icon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchBar } from "@/components/common/search-bar";
import { useIsMobile } from "@/hooks";
import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";
import type { User as SupabaseUser } from "@supabase/supabase-js";

interface HeaderProps {
  className?: string;
  showSearchBar?: boolean;
  /** Ana sayfa hero'su: header mavi hero panelinin parçası olur.
   *  En üstte (hero görünürken) mavi zemin + beyaz logo + beyaz pill butonlar;
   *  scroll'da katı beyaz + mavi logo. Mobilde de görünür (sticky DEĞİL, hero
   *  ile birlikte kayar — üstteki yapışkan aramayı MobileStickySearch verir). */
  overlay?: boolean;
  /** Mobilde de göster (beyaz, sticky DEĞİL — sayfayla kayar). Varsayılan: mobilde
   *  header yok, gezinmeyi alt menü üstlenir. Şimdilik yalnız /for-hosts kullanır. */
  showOnMobile?: boolean;
  /** Header'daki arama barının yanında ek içerik (ör. arama sonuçları sıralama
   *  butonu). Yalnız showSearchBar true iken görünür. */
  searchExtra?: ReactNode;
}

export function Header({
  className,
  showSearchBar = false,
  overlay = false,
  showOnMobile = false,
  searchExtra,
}: HeaderProps) {
  const isMobile = useIsMobile();
  const [user, setUser] = useState<SupabaseUser | null>(null);
  // headerSearchVisible mantığı: showSearchBar=false ise zaten gizli kalır.
  // showSearchBar=true + sentinel yok ise header search görünür (default true).
  // Sentinel varsa IntersectionObserver observe() çağrıldığı anda ilk callback'i
  // tetikler, bu sayede ayrı bir initial setState'e gerek kalmaz — React 19
  // set-state-in-effect kuralını ihlal etmez.
  const [headerSearchVisible, setHeaderSearchVisible] = useState(!overlay);

  useEffect(() => {
    if (!showSearchBar) return;

    const sentinel = document.querySelector("[data-hero-searchbar]");
    if (!sentinel) return; // sentinel yoksa default true kullanılır

    const observer = new IntersectionObserver(
      ([entry]) => setHeaderSearchVisible(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [showSearchBar]);

  useEffect(() => {
    const supabase = createClient();
    if (!supabase) return;

    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user);
    };

    getUser();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    const supabase = createClient();
    if (supabase) {
      await supabase.auth.signOut();
      window.location.href = "/";
    }
  };

  // Mobilde header yalnız ana sayfa hero'sunda (overlay) görünür; diğer
  // sayfalarda mobil gezinmeyi alt menü (MobileBottomNav) üstlenir.
  if (isMobile && !overlay && !showOnMobile) return null;
  // Ana sayfa hero'su dışında mobilde açılan beyaz header (konum ana sayfayla aynı)
  const mobileSolid = showOnMobile && !overlay;

  // overlay modunda: en üstte (hero search görünürken) hero ile aynı mavi; scroll sonrası katı.
  const transparent = overlay && !headerSearchVisible;

  // "Evinizi Listeleyin" pill'i: hero'da (mavi zemin) beyaz pill + mavi yazı; katı
  // header'da (beyaz zemin) ince çerçeveli pill. Header'da kompakt arama da
  // görünürken dar ekranda gizlenir — yoksa arama sıkışıp alanlarını iki satıra
  // kırıyor (/listings'te ölçüldü). Menüde zaten var → işlev kaybı yok.
  const compactSearchShown = showSearchBar && headerSearchVisible;
  const pillClass = cn(
    "h-10 rounded-full px-4 text-sm font-semibold",
    transparent
      ? "bg-white text-primary shadow-sm hover:bg-white/90 hover:text-primary"
      : "border-border text-foreground hover:border-primary hover:text-primary border"
  );

  // Menü butonu: mobilde 44px (dokunma hedefi), masaüstünde 40px.
  const menuTriggerClass = cn(
    "h-11 w-11 rounded-full sm:h-10 sm:w-10",
    transparent
      ? "text-primary hover:text-primary border-white bg-white shadow-sm hover:bg-white/90"
      : "border-border"
  );

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-colors duration-300",
        transparent ? "bg-primary" : "bg-white",
        overlay && !transparent && "shadow-sm",
        (overlay || showOnMobile) && "max-sm:static",
        className
      )}
    >
      {/* Mobilde px-8: header mobilde yalnız ana sayfa hero'sunda çizilir; logo ve menü
          butonu arama kartının İÇ hizasına (ikon daireleri / ok işaretleri) oturur. */}
      <div
        className={cn(
          "mx-auto flex h-20 max-w-7xl items-center justify-between sm:px-6 lg:px-8",
          // Mobil: ana sayfa hero'su ve mobilde açılan header AYNI konumda (h-20,
          // px-8 → logo/menü kenardan 32px içeride, dikey orta 40px).
          overlay || mobileSolid ? "px-8" : "px-4"
        )}
      >
        {/* Hero'da negatif margin: PNG'nin kendi saydam sol boşluğunu (yüksekliğin
            ~%25'i) telafi eder →
            logo, başlık ve arama barıyla aynı sol hizada durur. Margin img'de DEĞİL
            Link'te olmalı: img'ye verilince flex onu o kadar daraltıp logoyu eziyordu. */}
        <Link
          href="/"
          className={cn(
            "flex shrink-0 items-center",
            transparent && "-ml-4",
            mobileSolid && "max-sm:-ml-4"
          )}
          aria-label="konakla ana sayfa"
        >
          {/* Hero'da (mavi zemin) beyaz logo h-16 (mobil + masaüstü); katı header'da mavi
              logo (h-14). PNG'nin yalnız ~%53'ü görünür yazı (gerisi saydam boşluk) →
              h-16 ≈ 34px görünür logo. Sol saydam boşluk = yüksekliğin %25'i → -ml-4. */}
          <Image
            src={transparent ? "/logo_beyaz.png" : "/logo_mavi.png"}
            // Link'in aria-label'ı ekran okuyucuya zaten "konakla ana sayfa" der;
            // alt yine dolu: boş alt SEO denetimlerinde "alt'sız görsel" sayılıyor.
            alt="konakla"
            width={300}
            height={90}
            className={cn(
              "w-auto transition-all duration-300",
              transparent ? "h-16" : "h-14",
              mobileSolid && "max-sm:h-16"
            )}
            priority
          />
        </Link>

        {showSearchBar && (
          <div
            className={cn(
              "mx-6 flex flex-1 items-center justify-center gap-2 transition-all duration-300 max-sm:hidden",
              headerSearchVisible
                ? "translate-y-0 opacity-100"
                : // Görünmezken de yer kaplıyor; tablette logo + pill'lerle sığmadığı
                  // için (yatay kaydırma) lg altında yerden de kalkar.
                  "pointer-events-none -translate-y-2 opacity-0 max-lg:hidden"
            )}
          >
            <SearchBar variant="compact" />
            {searchExtra}
          </div>
        )}

        <div className="flex shrink-0 items-center gap-3 sm:gap-4">
          {/* Mobilde gizli — alt menüde zaten "Evini Listele" var. */}
          <Link
            href="/for-hosts"
            className={cn("max-sm:hidden", compactSearchShown && "max-xl:hidden")}
          >
            <Button variant="ghost" className={pillClass}>
              Evinizi Listeleyin
            </Button>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Menü" className={menuTriggerClass}>
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl p-1.5">
              {user ? (
                <>
                  <DropdownMenuItem asChild>
                    <Link href="/profile" className="flex items-center gap-3">
                      <SvgMaskIcon src="/profil.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Profilim</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/favorites" className="flex items-center gap-3">
                      <SvgMaskIcon src="/favoriler.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Favoriler</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/for-hosts" className="flex items-center gap-3">
                      <SvgMaskIcon src="/ekle.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Evinizi Listeleyin</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/about" className="flex items-center gap-3">
                      <SvgMaskIcon src="/anasayfa.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Hakkımızda</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/blog" className="flex items-center gap-3">
                      <Newspaper className="text-muted-foreground h-4 w-4" />
                      <span>Blog</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="text-destructive flex items-center gap-3"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Çıkış Yap</span>
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem asChild>
                    <Link href="/auth" className="flex items-center gap-3">
                      <LogIn className="text-muted-foreground h-4 w-4" />
                      <span>Giriş Yap</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/auth" className="flex items-center gap-3">
                      <UserPlus className="text-muted-foreground h-4 w-4" />
                      <span>Kayıt Ol</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/favorites" className="flex items-center gap-3">
                      <SvgMaskIcon src="/favoriler.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Favoriler</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/for-hosts" className="flex items-center gap-3">
                      <SvgMaskIcon src="/ekle.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Evinizi Listeleyin</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/about" className="flex items-center gap-3">
                      <SvgMaskIcon src="/anasayfa.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Hakkımızda</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/blog" className="flex items-center gap-3">
                      <Newspaper className="text-muted-foreground h-4 w-4" />
                      <span>Blog</span>
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
